import React from 'react';
import { useField } from 'formik';
import { FieldWrapper, FieldWrapperProps } from './FieldWrapper';
import { SelectionTags, SelectionTagsProps, SelectionTagType, TileValue } from '../SelectionTags';

export interface WrappedUnconnectedSelectionTagsProps<SchemaType extends SelectionTagType> {
  inputProps: SelectionTagsProps<SchemaType>;
  wrapperProps?: FieldWrapperProps;
}

export const WrappedUnconnectedSelectionTags = <SchemaType extends SelectionTagType>({
  wrapperProps,
  inputProps,
}: WrappedUnconnectedSelectionTagsProps<SchemaType>) => {
  return (
    <FieldWrapper {...wrapperProps}>
      <SelectionTags {...inputProps} />
    </FieldWrapper>
  );
};

type ControlledSelectionTagsProps<SchemaType extends SelectionTagType> = Omit<
  SelectionTagsProps<SchemaType>,
  'onSelected' | 'selectedValue'
>;

export interface SelectionTagsInputProps<SchemaType extends SelectionTagType> {
  /** full props for the underlying SelectionTags component */
  inputProps?: ControlledSelectionTagsProps<SchemaType>;
  /** full props for the underlying FieldWrapper component */
  wrapperProps?: FieldWrapperProps;
  /** name of the form field in the Formik form values */
  name: string;
  /** disable the selection tag buttons */
  disabled?: boolean;
  /** label for the form field */
  label: FieldWrapperProps['label'];
  /** help text for the form field */
  helpText?: FieldWrapperProps['helpText'];
  /** help text for the form field but above the tags */
  helpTextAbove?: FieldWrapperProps['helpText'];
  /** tooltip for the form field */
  tooltip?: FieldWrapperProps['tooltip'];
  /** type of selection tags to render (letter or number) */
  type: ControlledSelectionTagsProps<SchemaType>['type'];
  /** size for the SelectionTags */
  buttonSize?: ControlledSelectionTagsProps<SchemaType>['buttonSize'];
  /** requires the field to be touched before it shows a validation error, defaults to false */
  validationRequiresTouched?: boolean;
}

/**
 * A group of selection tag button options
 * @name SelectionTagsInput
 * @description this should be used as the normal component for selection tags for all Formik forms.
 * it will automatically render any validation errors and will correctly set a11y roles for the elements
 *
 * @example
 * <SelectionTagsInput
 *  name="input"
 *  label={t('common:my.label.key')}
 *  type="number"
 * />
 */
export const SelectionTagsInput = <SchemaType extends SelectionTagType>({
  buttonSize = 'lg',
  disabled,
  helpText,
  helpTextAbove,
  inputProps,
  label,
  name,
  tooltip,
  type,
  validationRequiresTouched = false,
  wrapperProps,
}: SelectionTagsInputProps<SchemaType>) => {
  const [input, meta, helpers] = useField<TileValue<SchemaType>>(name);
  const { value: formikValue } = input;
  const { error, touched } = meta;
  const hasError = !!error;
  const isTouched = !!touched;
  const isInvalid = !validationRequiresTouched ? hasError : hasError && isTouched;
  const wrapperValidation = isInvalid ? { validation: { valid: !hasError, message: error?.toString() } } : {};
  const inputValue = formikValue ?? '';

  const onSelected = (newVal: string) => {
    helpers.setValue(newVal as TileValue<SchemaType>);
  };

  return (
    <WrappedUnconnectedSelectionTags
      inputProps={{
        ...inputProps,
        buttonSize,
        disabled,
        onSelected,
        selectedValue: inputValue,
        type,
      }}
      wrapperProps={{
        ...wrapperProps,
        ...wrapperValidation,
        disabled,
        helpText,
        helpTextAbove,
        id: name,
        label,
        tooltip,
      }}
    />
  );
};
